import type {
  DebugAuditCheckpoint,
  DebugAuditConfig,
  DebugAuditFinalDigest,
  DebugAuditState,
  SessionReportIncident,
  SessionReportPhase,
  SessionReportProvider,
  SessionReportTranscriptMilestone,
} from "./types";

type StoredDebugAuditSession = DebugAuditConfig & {
  sessionId: string;
  createdAt: number;
  startedAt: number | null;
  stoppedAt: number | null;
  requestedProvider: SessionReportProvider;
  resolvedProvider: SessionReportProvider;
};

interface IDebugAuditStore {
  createSession(record: StoredDebugAuditSession): Promise<void>;
  updateSession(
    sessionId: string,
    patch: Partial<StoredDebugAuditSession>
  ): Promise<void>;
  appendPhase(
    sessionId: string,
    sequence: number,
    phase: SessionReportPhase
  ): Promise<void>;
  appendIncident(
    sessionId: string,
    sequence: number,
    incident: SessionReportIncident
  ): Promise<void>;
  putMilestone(
    sessionId: string,
    milestone: SessionReportTranscriptMilestone
  ): Promise<void>;
  appendCheckpoint(
    sessionId: string,
    sequence: number,
    checkpoint: DebugAuditCheckpoint
  ): Promise<void>;
  appendFinalDigest(
    sessionId: string,
    sequence: number,
    digest: DebugAuditFinalDigest
  ): Promise<void>;
  getSessionSnapshot(sessionId: string): Promise<DebugAuditState | null>;
  clear(sessionId: string): Promise<void>;
}

const sortByTimestamp = <T extends { timestamp: number }>(values: T[]): T[] =>
  values.slice().sort((left, right) => left.timestamp - right.timestamp);

class InMemoryDebugAuditStore implements IDebugAuditStore {
  private readonly sessions = new Map<string, StoredDebugAuditSession>();
  private readonly phases = new Map<string, SessionReportPhase[]>();
  private readonly incidents = new Map<string, SessionReportIncident[]>();
  private readonly milestones = new Map<string, Map<string, SessionReportTranscriptMilestone>>();
  private readonly checkpoints = new Map<string, DebugAuditCheckpoint[]>();
  private readonly finalDigests = new Map<string, DebugAuditFinalDigest[]>();

  async createSession(record: StoredDebugAuditSession): Promise<void> {
    this.sessions.set(record.sessionId, { ...record });
  }

  async updateSession(
    sessionId: string,
    patch: Partial<StoredDebugAuditSession>
  ): Promise<void> {
    const current = this.sessions.get(sessionId);

    if (!current) {
      return;
    }

    this.sessions.set(sessionId, {
      ...current,
      ...patch,
      sessionId,
    });
  }

  async appendPhase(
    sessionId: string,
    _sequence: number,
    phase: SessionReportPhase
  ): Promise<void> {
    const phases = this.phases.get(sessionId) ?? [];
    phases.push({ ...phase });
    this.phases.set(sessionId, phases);
  }

  async appendIncident(
    sessionId: string,
    _sequence: number,
    incident: SessionReportIncident
  ): Promise<void> {
    const incidents = this.incidents.get(sessionId) ?? [];
    incidents.push({ ...incident });
    this.incidents.set(sessionId, incidents);
  }

  async putMilestone(
    sessionId: string,
    milestone: SessionReportTranscriptMilestone
  ): Promise<void> {
    const milestones = this.milestones.get(sessionId) ?? new Map();
    milestones.set(milestone.type, { ...milestone });
    this.milestones.set(sessionId, milestones);
  }

  async appendCheckpoint(
    sessionId: string,
    _sequence: number,
    checkpoint: DebugAuditCheckpoint
  ): Promise<void> {
    const checkpoints = this.checkpoints.get(sessionId) ?? [];
    checkpoints.push({
      ...checkpoint,
      resilienceStatus: checkpoint.resilienceStatus
        ? { ...checkpoint.resilienceStatus }
        : null,
      metricsSnapshot: checkpoint.metricsSnapshot
        ? JSON.parse(JSON.stringify(checkpoint.metricsSnapshot))
        : null,
    });
    this.checkpoints.set(sessionId, checkpoints);
  }

  async appendFinalDigest(
    sessionId: string,
    _sequence: number,
    digest: DebugAuditFinalDigest
  ): Promise<void> {
    const digests = this.finalDigests.get(sessionId) ?? [];
    digests.push({ ...digest });
    this.finalDigests.set(sessionId, digests);
  }

  async getSessionSnapshot(sessionId: string): Promise<DebugAuditState | null> {
    const session = this.sessions.get(sessionId);

    if (!session) {
      return null;
    }

    const milestones = Array.from(this.milestones.get(sessionId)?.values() ?? []);

    return {
      ...session,
      phases: sortByTimestamp(this.phases.get(sessionId) ?? []),
      incidents: sortByTimestamp(this.incidents.get(sessionId) ?? []),
      transcriptMilestones: sortByTimestamp(milestones),
      checkpoints: sortByTimestamp(this.checkpoints.get(sessionId) ?? []),
      finalDigests: sortByTimestamp(this.finalDigests.get(sessionId) ?? []),
    };
  }

  async clear(sessionId: string): Promise<void> {
    this.sessions.delete(sessionId);
    this.phases.delete(sessionId);
    this.incidents.delete(sessionId);
    this.milestones.delete(sessionId);
    this.checkpoints.delete(sessionId);
    this.finalDigests.delete(sessionId);
  }
}

class IndexedDbDebugAuditStore implements IDebugAuditStore {
  private static readonly DATABASE_NAME = "sofya.transcription.debugAudit";
  private static readonly DATABASE_VERSION = 1;
  private static readonly SESSIONS_STORE = "sessions";
  private static readonly PHASES_STORE = "phases";
  private static readonly INCIDENTS_STORE = "incidents";
  private static readonly MILESTONES_STORE = "milestones";
  private static readonly CHECKPOINTS_STORE = "checkpoints";
  private static readonly FINAL_DIGESTS_STORE = "finalDigests";
  private databasePromise: Promise<IDBDatabase> | null = null;

  async createSession(record: StoredDebugAuditSession): Promise<void> {
    const database = await this.getDatabase();

    await this.runTransaction(database, "readwrite", (stores) => {
      stores.sessions.put(record, record.sessionId);
    });
  }

  async updateSession(
    sessionId: string,
    patch: Partial<StoredDebugAuditSession>
  ): Promise<void> {
    const current = await this.getSessionRecord(sessionId);

    if (!current) {
      return;
    }

    const database = await this.getDatabase();
    await this.runTransaction(database, "readwrite", (stores) => {
      stores.sessions.put(
        {
          ...current,
          ...patch,
          sessionId,
        },
        sessionId
      );
    });
  }

  async appendPhase(
    sessionId: string,
    sequence: number,
    phase: SessionReportPhase
  ): Promise<void> {
    const database = await this.getDatabase();

    await this.runTransaction(database, "readwrite", (stores) => {
      stores.phases.put(phase, [sessionId, sequence]);
    });
  }

  async appendIncident(
    sessionId: string,
    sequence: number,
    incident: SessionReportIncident
  ): Promise<void> {
    const database = await this.getDatabase();

    await this.runTransaction(database, "readwrite", (stores) => {
      stores.incidents.put(incident, [sessionId, sequence]);
    });
  }

  async putMilestone(
    sessionId: string,
    milestone: SessionReportTranscriptMilestone
  ): Promise<void> {
    const database = await this.getDatabase();

    await this.runTransaction(database, "readwrite", (stores) => {
      stores.milestones.put(milestone, [sessionId, milestone.type]);
    });
  }

  async appendCheckpoint(
    sessionId: string,
    sequence: number,
    checkpoint: DebugAuditCheckpoint
  ): Promise<void> {
    const database = await this.getDatabase();

    await this.runTransaction(database, "readwrite", (stores) => {
      stores.checkpoints.put(checkpoint, [sessionId, sequence]);
    });
  }

  async appendFinalDigest(
    sessionId: string,
    sequence: number,
    digest: DebugAuditFinalDigest
  ): Promise<void> {
    const database = await this.getDatabase();

    await this.runTransaction(database, "readwrite", (stores) => {
      stores.finalDigests.put(digest, [sessionId, sequence]);
    });
  }

  async getSessionSnapshot(sessionId: string): Promise<DebugAuditState | null> {
    const session = await this.getSessionRecord(sessionId);

    if (!session) {
      return null;
    }

    const database = await this.getDatabase();
    const [phases, incidents, milestones, checkpoints, finalDigests] =
      await Promise.all([
        this.getRangeValues<SessionReportPhase>(
          database,
          IndexedDbDebugAuditStore.PHASES_STORE,
          IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER])
        ),
        this.getRangeValues<SessionReportIncident>(
          database,
          IndexedDbDebugAuditStore.INCIDENTS_STORE,
          IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER])
        ),
        this.getRangeValues<SessionReportTranscriptMilestone>(
          database,
          IndexedDbDebugAuditStore.MILESTONES_STORE,
          IDBKeyRange.bound([sessionId, ""], [sessionId, "\uffff"])
        ),
        this.getRangeValues<DebugAuditCheckpoint>(
          database,
          IndexedDbDebugAuditStore.CHECKPOINTS_STORE,
          IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER])
        ),
        this.getRangeValues<DebugAuditFinalDigest>(
          database,
          IndexedDbDebugAuditStore.FINAL_DIGESTS_STORE,
          IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER])
        ),
      ]);

    return {
      ...session,
      phases: sortByTimestamp(phases),
      incidents: sortByTimestamp(incidents),
      transcriptMilestones: sortByTimestamp(milestones),
      checkpoints: sortByTimestamp(checkpoints),
      finalDigests: sortByTimestamp(finalDigests),
    };
  }

  async clear(sessionId: string): Promise<void> {
    const database = await this.getDatabase();

    await this.runTransaction(database, "readwrite", (stores) => {
      stores.sessions.delete(sessionId);
      stores.phases.delete(IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER]));
      stores.incidents.delete(
        IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER])
      );
      stores.milestones.delete(IDBKeyRange.bound([sessionId, ""], [sessionId, "\uffff"]));
      stores.checkpoints.delete(
        IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER])
      );
      stores.finalDigests.delete(
        IDBKeyRange.bound([sessionId, 0], [sessionId, Number.MAX_SAFE_INTEGER])
      );
    });
  }

  private async getDatabase(): Promise<IDBDatabase> {
    if (!this.databasePromise) {
      this.databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(
          IndexedDbDebugAuditStore.DATABASE_NAME,
          IndexedDbDebugAuditStore.DATABASE_VERSION
        );

        request.onupgradeneeded = () => {
          const database = request.result;
          this.ensureStore(database, IndexedDbDebugAuditStore.SESSIONS_STORE);
          this.ensureStore(database, IndexedDbDebugAuditStore.PHASES_STORE);
          this.ensureStore(database, IndexedDbDebugAuditStore.INCIDENTS_STORE);
          this.ensureStore(database, IndexedDbDebugAuditStore.MILESTONES_STORE);
          this.ensureStore(database, IndexedDbDebugAuditStore.CHECKPOINTS_STORE);
          this.ensureStore(database, IndexedDbDebugAuditStore.FINAL_DIGESTS_STORE);
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }

    return this.databasePromise;
  }

  private ensureStore(database: IDBDatabase, storeName: string) {
    if (!database.objectStoreNames.contains(storeName)) {
      database.createObjectStore(storeName);
    }
  }

  private async getSessionRecord(
    sessionId: string
  ): Promise<StoredDebugAuditSession | null> {
    const database = await this.getDatabase();

    return new Promise<StoredDebugAuditSession | null>((resolve, reject) => {
      const transaction = database.transaction(
        IndexedDbDebugAuditStore.SESSIONS_STORE,
        "readonly"
      );
      const store = transaction.objectStore(IndexedDbDebugAuditStore.SESSIONS_STORE);
      const request = store.get(sessionId);

      request.onsuccess = () => resolve((request.result as StoredDebugAuditSession) ?? null);
      request.onerror = () => reject(request.error);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  private async getRangeValues<T>(
    database: IDBDatabase,
    storeName: string,
    range: IDBKeyRange
  ): Promise<T[]> {
    return new Promise<T[]>((resolve, reject) => {
      const transaction = database.transaction(storeName, "readonly");
      const store = transaction.objectStore(storeName);
      const request = store.openCursor(range);
      const values: T[] = [];

      request.onsuccess = () => {
        const cursor = request.result;

        if (!cursor) {
          resolve(values);
          return;
        }

        values.push(cursor.value as T);
        cursor.continue();
      };

      request.onerror = () => reject(request.error);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  private async runTransaction(
    database: IDBDatabase,
    mode: IDBTransactionMode,
    callback: (stores: {
      sessions: IDBObjectStore;
      phases: IDBObjectStore;
      incidents: IDBObjectStore;
      milestones: IDBObjectStore;
      checkpoints: IDBObjectStore;
      finalDigests: IDBObjectStore;
    }) => void
  ): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(
        [
          IndexedDbDebugAuditStore.SESSIONS_STORE,
          IndexedDbDebugAuditStore.PHASES_STORE,
          IndexedDbDebugAuditStore.INCIDENTS_STORE,
          IndexedDbDebugAuditStore.MILESTONES_STORE,
          IndexedDbDebugAuditStore.CHECKPOINTS_STORE,
          IndexedDbDebugAuditStore.FINAL_DIGESTS_STORE,
        ],
        mode
      );

      callback({
        sessions: transaction.objectStore(IndexedDbDebugAuditStore.SESSIONS_STORE),
        phases: transaction.objectStore(IndexedDbDebugAuditStore.PHASES_STORE),
        incidents: transaction.objectStore(IndexedDbDebugAuditStore.INCIDENTS_STORE),
        milestones: transaction.objectStore(IndexedDbDebugAuditStore.MILESTONES_STORE),
        checkpoints: transaction.objectStore(IndexedDbDebugAuditStore.CHECKPOINTS_STORE),
        finalDigests: transaction.objectStore(IndexedDbDebugAuditStore.FINAL_DIGESTS_STORE),
      });

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }
}

export const createDebugAuditStore = (): IDebugAuditStore => {
  if (typeof indexedDB !== "undefined") {
    return new IndexedDbDebugAuditStore();
  }

  return new InMemoryDebugAuditStore();
};
